import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Card, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faMinus } from "@fortawesome/free-solid-svg-icons";
import { useCartStore } from "../../../../zustand/cartStore";
import { useAuthStore } from "../../../../zustand/loginStore";
import CustomNavbarClients from "../navBarClients/CustomNavbarClients";
import ClientMenuBar from "../ClientMenuBar";
import Footer from "../Footer";
import RelatedProducts from "../relatedProducts/RelatedProducts";
import { useBreakpoints } from "hooks/useBreakPoints";
import { primaryColor } from "utils/colors";
import TrashIcon from "components/icons/TrashIcon";
import "./CartPage.scss";
import CustomCheckbox from "components/general/CustomCheckbox";
import CartIcon from "components/icons/CartIcon";
import ImageModal from "components/general/ImageModal";
import OrderSummary from "./OrderSummary";
import { useNavigate } from "react-router-dom";
import CustomToast from "components/general/CustomToast";

/**
 * Shopping cart page component that displays cart items, allows quantity modifications,
 * and provides checkout functionality with authentication flow
 * @returns {JSX.Element} The cart page component
 */
const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const { items, removeItem, updateQuantity } = useCartStore();
  const { isLoggedIn } = useAuthStore();
  const { isMobile } = useBreakpoints();
  
  /**
   * State to track selected items in the cart
   * Initializes with all items selected by default
   */
  const [selectedItems, setSelectedItems] = useState<{
    [key: string]: boolean;
  }>(
    items.reduce((acc, item) => {
      acc[`${item.productId}-${item.colorIndex}-${item.sizeId}`] = true;
      return acc;
    }, {} as { [key: string]: boolean })
  );

  // Actualizar selectedItems cuando cambien los items del carrito
  useEffect(() => {
    setSelectedItems((prevSelectedItems) => {
      return items.reduce((acc, item) => {
        const itemKey = `${item.productId}-${item.colorIndex}-${item.sizeId}`;
        // Mantener el estado actual si existe, o establecer como true si es nuevo
        acc[itemKey] = prevSelectedItems[itemKey] !== undefined ? prevSelectedItems[itemKey] : true;
        return acc;
      }, {} as { [key: string]: boolean });
    });
  }, [items]);
  
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showToast, setShowToast] = useState<{
    message: string;
    show: boolean;
    success: boolean;
  }>({ message: "", show: false, success: true });

  /**
   * Calculates the subtotal of selected items in the cart
   */
  const selectedItemsList = items.filter((item) => {
    const itemKey = `${item.productId}-${item.colorIndex}-${item.sizeId}`;
    return selectedItems[itemKey];
  });

  const subtotal = selectedItemsList.reduce(
    (sum, item) => sum + item.finalPrice * item.quantity,
    0
  );

  /**
   * Calculates the total number of selected items in the cart
   */
  const totalItems = selectedItemsList.reduce((sum, item) => sum + item.quantity, 0);

  const shippingCost = 50;
  const totalCost = subtotal;

  /**
   * Extracts unique category IDs from all cart items for related products
   * @returns {string} Comma-separated string of unique category IDs
   */
  const categoryIds = React.useMemo(() => {
    if (items.length === 0) return "";

    const uniqueCategoryIds = Array.from(
      new Set(items.flatMap((item) => item.categories.map((cat) => cat.id)))
    );

    return uniqueCategoryIds.join(",");
  }, [items]);

  /**
   * Handles checkbox change for item selection
   * @param {string} itemKey - Unique key identifying the cart item
   */
  const handleCheckboxChange = (itemKey: string) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemKey]: !prev[itemKey],
    }));
  };

  /**
   * Handles clicking on product images to open image modal
   * @param {string} imageSrc - Source URL of the image to display
   */
  const handleImageClick = (imageSrc: string) => {
    setSelectedImage(imageSrc);
    setSelectedImageIndex(0);
    setIsImageModalOpen(true);
  };

  /**
   * Handles quantity update with stock validation
   * @param {number} productId - Product ID
   * @param {number} colorIndex - Color index
   * @param {number} sizeId - Size ID
   * @param {number} sizeProductId - Size product ID
   * @param {number} newQuantity - New quantity to set
   */
  const handleQuantityUpdate = (
    productId: number,
    colorIndex: number,
    sizeId: number,
    sizeProductId: number,
    newQuantity: number
  ) => {
    const result = updateQuantity(
      productId,
      colorIndex,
      sizeId,
      sizeProductId,
      newQuantity
    );

    if (!result.success) {
      setShowToast({
        message: result.message || "Error al actualizar cantidad",
        show: true,
        success: false,
      });
    }
  };

  /**
   * Handles the checkout button click with authentication verification
   * Redirects to login if user is not authenticated, otherwise proceeds to checkout
   */
  const handleCheckoutClick = () => {
    const { token, isLoggedIn } = useAuthStore.getState();
    
    // Verificar que al menos un item esté seleccionado
    if (selectedItemsList.length === 0) {
      alert("Por favor, selecciona al menos un artículo para proceder al pago.");
      return;
    }
    
    if (!token || !isLoggedIn()) {
      navigate("/login", { 
        state: { returnUrl: "/checkout" } 
      });
    } else {
      // Crear un carrito temporal solo con los items seleccionados
      const tempCartItems = selectedItemsList;
      
      // Guardar los items seleccionados en sessionStorage para el checkout
      sessionStorage.setItem('checkoutItems', JSON.stringify(tempCartItems));
      
      navigate("/checkout");
    }
  };

  return (
    <>
      <CustomNavbarClients />
      <ClientMenuBar />
      <Container className="cart-page-container">
        <Row className="mt-4">
          {/* Resumen en móvil - aparece arriba */}
          {isMobile && (
            <Col lg={12} md={12}>
              <h1 className="summary-title">Resumen</h1>
              <div className="mobile-summary">
                <OrderSummary
                  totalItems={totalItems}
                  subtotal={subtotal}
                  shippingCost={shippingCost}
                  totalCost={totalCost}
                  buttonDisabled={selectedItemsList.length === 0}
                  onButtonClick={handleCheckoutClick}
                  showShippingCost={false}
                  buttonText={
                    !isLoggedIn() ? "Iniciar sesión" : "Proceder al pago"
                  }
                />
              </div>
              <h1 className="cart-title">Mi carrito</h1>
            </Col>
          )}
          
          <Col lg={8} md={12}>
            {/* Título solo en desktop */}
            {!isMobile && <h1 className="cart-title">Mi carrito</h1>}
            
            {items.length === 0 ? (
              <div className="empty-cart">
                <p>No hay artículos en tu carrito</p>
                <CartIcon size="7rem" />
                <p> Explora la tienda para añadir tus articulos favoritos!</p>
              </div>
            ) : (
              items.map((item) => {
                const itemKey = `${item.productId}-${item.colorIndex}-${item.sizeId}`;
                return (
                  <div className="cart-item-row" key={itemKey}>
                    {/* Checkbox original para desktop */}
                    {!isMobile && (
                      <div className="item-checkbox">
                        <CustomCheckbox
                          checked={selectedItems[itemKey] || false}
                          onChange={() => handleCheckboxChange(itemKey)}
                        />
                      </div>
                    )}
                    
                    <Card className="cart-item-card mb-3">
                      {/* Checkbox sobrepuesto solo para móvil */}
                      {isMobile && (
                        <div className="item-checkbox-overlay">
                          <CustomCheckbox
                            checked={selectedItems[itemKey] || false}
                            onChange={() => handleCheckboxChange(itemKey)}
                          />
                        </div>
                      )}
                      
                      <Row className="no-gutters align-items-center">
                        <Col
                          xs={3}
                          md={2}
                          className="cart-item-image-container"
                        >
                          <div className="square-image-container">
                            <img
                              src={item.image}
                              alt={item.name}
                              className="cart-item-image"
                              onClick={() => handleImageClick(item.image)}
                            />
                          </div>
                        </Col>
                        <Col xs={8} md={6} className="cart-item-details">
                          <h5 className="cart-item-name">{item.name}</h5>
                          <p className="cart-item-color">
                            Color: {item.colorName}
                          </p>
                          <p className="cart-item-size">
                            Talla: {item.sizeName}
                          </p>
                          {/* Precio en móvil debajo de la talla */}
                          {isMobile && (
                            <div className="cart-item-price-mobile">
                              {item.discount && item.discount > 0 ? (
                                <div className="price-with-discount-mobile">
                                  <span className="discount-badge-mobile">
                                    -{item.discountType?.includes('%') ? 
                                      `${item.discount}%` : 
                                      `$${item.discount}`
                                    }
                                  </span>
                                  <p className="original-price-mobile">
                                    $
                                    {(item.originalPrice * item.quantity).toLocaleString(
                                      "es-MX",
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )}
                                  </p>
                                  <div className="discounted-price-container">
                                    <p className="discounted-price-mobile">
                                      $
                                      {(item.finalPrice * item.quantity).toLocaleString(
                                        "es-MX",
                                        {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        }
                                      )}
                                    </p>
                                  </div>
                                </div>
                              ) : (
                                <p className="price-value-mobile">
                                  $
                                  {(item.price * item.quantity).toLocaleString(
                                    "es-MX",
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    }
                                  )}
                                </p>
                              )}
                            </div>
                          )}
                          {/* Controles de cantidad originales para desktop */}
                          {!isMobile && (
                            <div className="quantity-control">
                              <span className="quantity-label">Cantidad:</span>
                              <button
                                className="quantity-btn-minimal"
                                onClick={() =>
                                  handleQuantityUpdate(
                                    item.productId,
                                    item.colorIndex,
                                    item.sizeId,
                                    item.sizeProductId,
                                    Math.max(1, item.quantity - 1)
                                  )
                                }
                              >
                                <FontAwesomeIcon icon={faMinus} />
                              </button>
                              <span className="quantity-number">
                                {item.quantity}
                              </span>
                              <button
                                className={`quantity-btn-minimal ${
                                  item.quantity >= item.stock ? "disabled" : ""
                                } ${item.quantity >= item.stock ? "custom-tooltip" : ""}`}
                                onClick={() =>
                                  handleQuantityUpdate(
                                    item.productId,
                                    item.colorIndex,
                                    item.sizeId,
                                    item.sizeProductId,
                                    item.quantity + 1
                                  )
                                }

                                data-bs-toggle={item.quantity >= item.stock ? "tooltip" : ""}
                                data-bs-placement="top"
                                data-bs-title={item.quantity >= item.stock ? `No hay más stock disponible. Solo quedan ${item.stock} unidades.` : ""}
                              >
                                <FontAwesomeIcon icon={faPlus} />
                              </button>
                            </div>
                          )}
                        </Col>
                        <Col
                          xs={4}
                          md={4}
                          className="cart-item-price-container"
                        >
                          {/* Botón de eliminar original para desktop */}
                          {!isMobile && (
                            <button
                              className="remove-item-btn"
                              onClick={() =>
                                removeItem(
                                  item.productId,
                                  item.colorIndex,
                                  item.sizeId,
                                  item.sizeProductId
                                )
                              }
                            >
                              <TrashIcon size={22} color="#614900" />
                            </button>
                          )}
                          
                          {/* Precio solo para desktop */}
                          {!isMobile && (
                            <div className="cart-item-price">
                              {item.discount && item.discount > 0 ? (
                                <div className="price-with-discount">
                                  <span className="discount-badge">
                                    -{item.discountType?.includes('%') ? 
                                      `${item.discount}%` : 
                                      `$${item.discount}`
                                    }
                                  </span>
                                  <p className="original-price">
                                    $
                                    {(item.originalPrice * item.quantity).toLocaleString(
                                      "es-MX",
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )}
                                  </p>
                                  <div className="price-row">
                                    <p className="price-label">Precio:</p>
                                    <p className="discounted-price">
                                      $
                                      {(item.finalPrice * item.quantity).toLocaleString(
                                        "es-MX",
                                        {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        }
                                      )}
                                    </p>
                                  </div>
                                </div>
                              ) : (
                                <div className="price-row">
                                  <p className="price-label">Precio:</p>
                                  <p className="price-value">
                                    $
                                    {(item.price * item.quantity).toLocaleString(
                                      "es-MX",
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </Col>
                      </Row>
                      
                      {/* Controles de cantidad y botón de eliminar solo para móvil */}
                      {isMobile && (
                        <div className="mobile-controls">
                          <button
                            className="remove-item-btn"
                            onClick={() =>
                              removeItem(
                                item.productId,
                                item.colorIndex,
                                item.sizeId,
                                item.sizeProductId
                              )
                            }
                          >
                            <TrashIcon size={22} color="#614900" />
                          </button>
                          <div className="quantity-control-mobile">
                            <button
                              className="quantity-btn-minimal"
                              onClick={() =>
                                handleQuantityUpdate(
                                  item.productId,
                                  item.colorIndex,
                                  item.sizeId,
                                  item.sizeProductId,
                                  Math.max(1, item.quantity - 1)
                                )
                              }
                            >
                              <FontAwesomeIcon icon={faMinus} />
                            </button>
                            <span className="quantity-number">
                              {item.quantity}
                            </span>
                            <button
                              className={`quantity-btn-minimal ${
                                item.quantity >= item.stock ? "disabled" : ""
                              } ${item.quantity >= item.stock ? "custom-tooltip" : ""}`}
                              onClick={() =>
                                handleQuantityUpdate(
                                  item.productId,
                                  item.colorIndex,
                                  item.sizeId,
                                  item.sizeProductId,
                                  item.quantity + 1
                                )
                              }

                              data-bs-toggle={item.quantity >= item.stock ? "tooltip" : ""}
                              data-bs-placement="top"
                              data-bs-title={item.quantity >= item.stock ? `No hay más stock disponible. Solo quedan ${item.stock} unidades.` : ""}
                            >
                              <FontAwesomeIcon icon={faPlus} />
                            </button>
                          </div>
                        </div>
                      )}
                    </Card>
                  </div>
                );
              })
            )}
          </Col>
          
          {/* Resumen en desktop - aparece a la derecha */}
          {!isMobile && (
            <Col lg={4} md={12}>
              <h1 className="summary-title">Resumen</h1>
              <OrderSummary
                totalItems={totalItems}
                subtotal={subtotal}
                shippingCost={shippingCost}
                totalCost={totalCost}
                buttonDisabled={selectedItemsList.length === 0}
                onButtonClick={handleCheckoutClick}
                showShippingCost={false}
                buttonText={
                  !isLoggedIn() ? "Iniciar sesión" : "Proceder al pago"
                }
              />
            </Col>
          )}
        </Row>
      </Container>

      {selectedImage && (
        <ImageModal
          isOpen={isImageModalOpen}
          onClose={() => setIsImageModalOpen(false)}
          images={[selectedImage]}
          selectedIndex={selectedImageIndex}
          onSelectImage={setSelectedImageIndex}
          isSingleImage={true}
        />
      )}

      <div className="d-flex align-items-center justify-content-center related-products-header">
        {!isMobile && (
          <div
            style={{
              height: "1px",
              background: primaryColor,
              flex: 1,
              maxWidth: "32%",
              boxShadow: "2px 4px 3px rgba(0,0,0,0.6)",
            }}
          />
        )}
        <h1
          style={{
            color: "black",
            fontWeight: "bold",
            fontSize: isMobile ? "20px" : "44px",
            letterSpacing: "3%",
            display: "flex",
            alignItems: "center",
            marginLeft: isMobile ? "2rem" : "3rem",
            marginRight: isMobile ? "2rem" : "3rem",
          }}
        >
          Productos relacionados
        </h1>
        {!isMobile && (
          <div
            style={{
              height: "1px",
              background: primaryColor,
              flex: 1,
              maxWidth: "32%",
              boxShadow: "2px 4px 3px rgba(0,0,0,0.6)",
            }}
          />
        )}
      </div>

      <RelatedProducts categoryIds={categoryIds} />
      <Footer />

      {/* CustomToast component */}
      <CustomToast
        show={showToast.show}
        onClose={() =>
          setShowToast({
            message: "",
            show: false,
            success: true,
          })
        }
        message={showToast.message}
        width="300px"
        success={showToast.success}
      />
    </>
  );
};

export default CartPage;
